import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import performLogout from "../../utils/performLogout";
import { DASHBOARD_VERSION } from "../../utils/appVersion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChartArea,
    faRetweet,
    faUniversity,
    faCode,
    faWindowRestore,
    faLink,
    faCogs,
    faUserShield,
    faWallet,
    faPenToSquare,
    faBell,
    faSignOutAlt,
    faCrown,
    faHandshake,
    faBullhorn,
    faChartPie,
    faUsers,
    faEnvelopeOpenText,
    faEye,
    faSitemap,
} from "@fortawesome/free-solid-svg-icons";
import { faDiscord } from "@fortawesome/free-brands-svg-icons";
import { changePage, logout } from "../../store/actions";
import partnersLogo from "../../images/ISPartnersLogo.png";
import { usePrivy } from "@privy-io/react-auth";
import useConnectedWallet from "../../hooks/useConnectedWallet";
import { useAccountType, useAgencyView } from "../../hooks/useAccountType";
import { setViewPartner } from "../../utils/partnerApi";

const NAV_MAIN = [
    { label: "Dashboard", to: "/overview", page: "overview", icon: faChartArea },
    { label: "VIP Tiers", to: "/vip-tiers", page: "vip-tiers", icon: faCrown },
    { label: "Swap History", to: "/transactions", page: "transactions", icon: faRetweet },
    { label: "Balances", to: "/balances", page: "balances", icon: faUniversity },
];

// Integrator tools — hidden in KOL / agency mode (routes still resolve).
const NAV_RESOURCES = [
    { label: "API", to: "/api-reference", page: "api-reference", icon: faCode },
    { label: "Widget", to: "/widget", page: "widget", icon: faWindowRestore },
];

// Referral & Campaigns: everyone. Campaign Stats: KOL + agency.
const NAV_REFERRAL = [
    { label: "Referral & Campaigns", to: "/referral", page: "referral", icon: faLink },
];
const NAV_KOL = [
    { label: "Campaign Stats", to: "/campaigns/stats", page: "campaign-stats", icon: faChartPie },
];
const NAV_AGENCY = [
    { label: "Agency Overview", to: "/agency", page: "agency", icon: faBullhorn },
    { label: "Partners", to: "/agency/partners", page: "agency-partners", icon: faUsers },
    { label: "Invites", to: "/agency/invites", page: "agency-invites", icon: faEnvelopeOpenText },
];

const WHITELIST_SUBS = [
    { label: "API IPs", to: "/whitelisted-ips", page: "whitelisted-ips" },
    { label: "Widget URLs", to: "/whitelisted-websites", page: "whitelisted-websites" },
    { label: "Webhooks", to: "/webhooks", page: "webhooks" },
];

const NAV_MGMT = [
    { label: "Revenue Share", to: "/revenue-share", page: "revenue-share", icon: faHandshake },
    { label: "Account", to: "/account", page: "account", icon: faUserShield },
    { label: "Linked Wallets", to: "/linked-wallets", page: "linked-wallets", icon: faWallet },
    { label: "Withdraw Wallet", to: "/edit-wallet", page: "edit-wallet", icon: faPenToSquare },
    { label: "Security", to: "/security", page: "security", icon: faBell },
];
// Shown right after Revenue Share once the admin CMS enabled sub-affiliate fees for the partner.
const NAV_SUB_FEES = { label: "Sub-Partner Fees", to: "/sub-partner-fees", page: "sub-partner-fees", icon: faSitemap };

const MySidebar = ({ toggled, handleToggleSidebar }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [whitelistOpen, setWhitelistOpen] = useState(false);
    // Storage version check now runs once at app boot (src/index.js).
    const accountType = useAccountType();
    const agencyView = useAgencyView();
    const subFeesEnabled = useSelector((state) => Number(state.userReducer.user?.response?.subFeesEnabled) === 1);
    const navMgmt = subFeesEnabled ? [NAV_MGMT[0], NAV_SUB_FEES, ...NAV_MGMT.slice(1)] : NAV_MGMT;
    const isIntegrator = accountType === "partner";
    const isKol = accountType === "kol";
    const isAgency = accountType === "agency";

    const { logout: privyLogout } = usePrivy();
    const { address, isConnected } = useConnectedWallet();

    const handleLogout = () => performLogout({ privyLogout, dispatch, logout });

    const handleChangePage = (value) => {
        dispatch(changePage(value));
        // close mobile drawer after navigation
        if (toggled) handleToggleSidebar();
    };

    const isActive = (path) => location.pathname === path;

    // Auto-open the whitelisting group when one of its subpages is active
    useEffect(() => {
        const whitelistPaths = WHITELIST_SUBS.map((s) => s.to);
        if (whitelistPaths.includes(location.pathname)) {
            setWhitelistOpen(true);
        }
    }, [location.pathname]);

    const renderItem = (item) => (
        <Link
            key={item.to}
            to={item.to}
            className={`isw-item${isActive(item.to) ? " on" : ""}`}
            onClick={() => handleChangePage(item.page)}
        >
            <span className="isw-ico">
                <FontAwesomeIcon icon={item.icon} />
            </span>
            {item.label}
        </Link>
    );

    return (
        <>
            {toggled && (
                <div className="isw-side-backdrop" onClick={handleToggleSidebar} />
            )}
            <aside className={`isw-side${toggled ? " open" : ""}`}>
                <Link to="/overview" className="isw-side-logo" onClick={() => handleChangePage("overview")}>
                    <img src={partnersLogo} alt="InstaSwap Partners" />
                </Link>

                <nav className="isw-nav">
                    {isAgency && agencyView && (
                        <div className="isw-viewing">
                            <span className="isw-ico"><FontAwesomeIcon icon={faEye} /></span>
                            <span className="t">Viewing <b>{agencyView.name}</b></span>
                            <button type="button" onClick={() => setViewPartner(null)} title="Back to my account">×</button>
                        </div>
                    )}

                    {isAgency && !agencyView && (
                        <>
                            <div className="isw-grp">Agency</div>
                            {NAV_AGENCY.map(renderItem)}
                        </>
                    )}

                    <div className="isw-grp">Main</div>
                    {NAV_MAIN.filter((i) => !(agencyView && i.to === "/balances")).map(renderItem)}

                    <div className="isw-grp">{isIntegrator ? "Resources" : "Referral"}</div>
                    {isIntegrator && NAV_RESOURCES.map(renderItem)}
                    {NAV_REFERRAL.map(renderItem)}
                    {(isKol || isAgency) && NAV_KOL.map(renderItem)}

                    {isIntegrator && (
                        <>
                            <button
                                type="button"
                                className={`isw-item${WHITELIST_SUBS.some((s) => isActive(s.to)) ? " on" : ""}`}
                                onClick={() => setWhitelistOpen((prev) => !prev)}
                            >
                                <span className="isw-ico">
                                    <FontAwesomeIcon icon={faCogs} />
                                </span>
                                Whitelisting
                                <span className={`isw-arrow${whitelistOpen ? " open" : ""}`}>›</span>
                            </button>
                            {whitelistOpen && (
                                <div className="isw-subwrap">
                                    {WHITELIST_SUBS.map((sub) => (
                                        <Link
                                            key={sub.to}
                                            to={sub.to}
                                            className={`isw-sub${isActive(sub.to) ? " on" : ""}`}
                                            onClick={() => handleChangePage(sub.page)}
                                        >
                                            <span className="dot" />
                                            {sub.label}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {!agencyView && (
                        <>
                            <div className="isw-grp">Management</div>
                            {navMgmt.map(renderItem)}
                        </>
                    )}

                    {isConnected && address && (
                        <Link to="/linked-wallets" className="isw-item mono" onClick={() => handleChangePage("linked-wallets")}>
                            <span className="isw-ico">
                                <FontAwesomeIcon icon={faWallet} />
                            </span>
                            {address.slice(0, 6)}...{address.slice(-4)}
                        </Link>
                    )}

                    <button
                        type="button"
                        className="isw-item"
                        onClick={() => window.open("https://discord.gg/um6NazJSMk", "_blank")}
                    >
                        <span className="isw-ico">
                            <FontAwesomeIcon icon={faDiscord} />
                        </span>
                        Discord
                    </button>
                </nav>

                <div className="isw-side-foot">
                    <button type="button" className="isw-item logout" onClick={handleLogout}>
                        <span className="isw-ico">
                            <FontAwesomeIcon icon={faSignOutAlt} />
                        </span>
                        Logout
                    </button>
                    <span className="ver">Dashboard v{DASHBOARD_VERSION}</span>
                </div>
            </aside>
        </>
    );
};

export default MySidebar;
